import { plainMap } from '@ipfabric/utils/fp/plainMap';
import type { GraphAppEvents } from 'components/Graph/GraphApp/events/GraphAppEvents';
import { addGraphCommandEventListener } from 'components/Graph/commands/events/addGraphCommandEventListener';
import { removeGraphCommandEventListener } from 'components/Graph/commands/events/removeGraphCommandEventListener';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import type { GraphCommandEvents } from 'components/Graph/model/GraphCommandEvents';
import { pipe } from 'fp-ts/lib/function';
import { removeArrayElementMutable } from 'utils/array/removeArrayElementMutable';
import type {
  EventListenerContainer,
  TypedEventEmitter,
} from 'utils/events/TypedEventEmitter';
import type { CommandDispatcher } from 'utils/react/useCommandDispatcher';

type GraphCommandListener = GraphCommandEvents[keyof GraphCommandEvents];
type GraphCommandListenerMap = Map<
  keyof GraphCommandEvents,
  GraphCommandListener[]
>;

const getListenersForType = (
  eventType: keyof GraphCommandEvents,
): ((listenerMap: GraphCommandListenerMap) => GraphCommandListener[]) =>
  plainMap.getOrCreate(eventType, (): GraphCommandListener[] => []);

export const createBindCommandEventFunctions = (
  dispatch: CommandDispatcher<GraphCommandContext>,
  appEvents: TypedEventEmitter<GraphAppEvents>,
): {
  bindGraphCommandEventListener: EventListenerContainer<GraphCommandEvents>['addListener'];
  removeGraphCommandEventListener: EventListenerContainer<GraphCommandEvents>['removeListener'];
} => {
  const listenersByType: GraphCommandListenerMap = new Map();
  appEvents.addListener('beforeUnmount', () => {
    listenersByType.forEach((listeners, eventType) => {
      for (const listener of listeners) {
        dispatch(...removeGraphCommandEventListener(eventType, listener));
      }
    });
  });
  return {
    bindGraphCommandEventListener: (
      eventType: keyof GraphCommandEvents,
      listener: GraphCommandListener,
    ) => {
      dispatch(...addGraphCommandEventListener(eventType, listener));
      pipe(listenersByType, getListenersForType(eventType), (l) =>
        l.push(listener),
      );
    },
    removeGraphCommandEventListener: (
      eventType: keyof GraphCommandEvents,
      listener: GraphCommandListener,
    ) => {
      dispatch(...removeGraphCommandEventListener(eventType, listener));
      pipe(
        listenersByType,
        getListenersForType(eventType),
        removeArrayElementMutable(listener),
      );
    },
  };
};
